import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { UserDocumentParticipantRole } from "@/api/models"
import type { DirectorySearchResult } from "@/services/directory-search-service"

import { DirectorySearch } from "@/components/directory-search"
import { Share2, UserPlus } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import {
  useAddDocumentParticipant,
  useDocumentParticipants,
  useRemoveDocumentParticipant,
  useTransferDocumentOwnership,
  useUpdateDocumentParticipantRole,
} from "@/hooks/use-documents"
import { SettingsService } from "@/services/settings-service"

import { ParticipantList } from "../../projects/components/participant-list"

type ArtifactShareDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  documentId: string
  currentUserRole?: UserDocumentParticipantRole
  ownerUserId?: string
}

const ASSIGNABLE_ROLES: UserDocumentParticipantRole[] = ["reader", "editor"]

/**
 * Share dialog for artifacts (documents with a non-markdown artifactType, or any
 * artifact saved as a document). Unlike the document dialog it allows assigning
 * the `editor` role and changing an existing participant's role.
 */
export function ArtifactShareDialog({
  open,
  onOpenChange,
  documentId,
  currentUserRole,
  ownerUserId,
}: ArtifactShareDialogProps) {
  const { t } = useTranslation()
  const currentUserOid = SettingsService.getInstance().claims.oid

  const { data: participantsData, isLoading } = useDocumentParticipants(documentId)
  const addParticipant = useAddDocumentParticipant()
  const updateRole = useUpdateDocumentParticipantRole()
  const removeParticipant = useRemoveDocumentParticipant()
  const transferOwnership = useTransferDocumentOwnership()

  const [newRole, setNewRole] = useState<UserDocumentParticipantRole>("reader")

  const resolvedCurrentUserRole = participantsData?.currentUserRole ?? currentUserRole
  const resolvedOwnerUserId = participantsData?.ownerUserId ?? ownerUserId
  const canManage = resolvedCurrentUserRole === "owner"

  const existingOids = (
    participantsData?.participants.map((participant) => participant.oid) ?? []
  ).filter(Boolean) as string[]

  const handleAddParticipant = async (result: DirectorySearchResult) => {
    if (existingOids.includes(result.oid)) return
    await addParticipant.mutateAsync({
      documentId,
      participant: {
        oid: result.oid,
        displayName: result.displayName,
        type: result.type,
        role: newRole,
      },
    })
  }

  const handleRoleChange = async (participantOid: string, role: UserDocumentParticipantRole) => {
    await updateRole.mutateAsync({ documentId, participantOid, role })
  }

  const handleRemove = async (participantOid: string) => {
    await removeParticipant.mutateAsync({ documentId, participantOid })
  }

  const handleTransferOwnership = async (newOwnerOid: string) => {
    await transferOwnership.mutateAsync({ documentId, newOwnerOid })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="size-5" />
            {t("sharing.dialog_title_artifact", { defaultValue: "Share artifact" })}
          </DialogTitle>
          <DialogDescription>
            {t("sharing.dialog_description_artifact", {
              defaultValue: "Add users or groups to view or edit this artifact.",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {canManage && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <UserPlus className="text-muted-foreground size-4" />
                <Label className="text-sm font-medium">
                  {t("sharing.add_participant", { defaultValue: "Add People" })}
                </Label>
              </div>

              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <DirectorySearch
                    disabled={addParticipant.isPending}
                    selectedOids={existingOids}
                    onSelect={handleAddParticipant}
                  />
                </div>
                <Select
                  value={newRole}
                  onValueChange={(v) => setNewRole(v as UserDocumentParticipantRole)}
                >
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSIGNABLE_ROLES.map((role) => (
                      <SelectItem key={role} value={role}>
                        {t(`sharing.role_${role}`, { defaultValue: role })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Separator />
            </div>
          )}

          <div className="space-y-3">
            <Label className="text-sm font-medium">
              {t("sharing.participants", { defaultValue: "People with Access" })}
            </Label>
            <ParticipantList
              allowedRoles={ASSIGNABLE_ROLES}
              canManage={canManage}
              currentUserOid={currentUserOid}
              entityType="document"
              isCurrentUserOwner={resolvedCurrentUserRole === "owner"}
              isLoading={isLoading}
              ownerUserId={resolvedOwnerUserId}
              participants={participantsData?.participants ?? []}
              emptyParticipantsText={t("sharing.no_participants_document", {
                defaultValue: "No participants yet. Add users or groups to share this artifact.",
              })}
              onRemove={handleRemove}
              onRoleChange={canManage ? handleRoleChange : undefined}
              onTransferOwnership={handleTransferOwnership}
            />
          </div>

          <div className="bg-muted/50 text-muted-foreground rounded-md px-3 py-2 text-xs">
            <p className="mb-1 font-medium">
              {t("sharing.role_descriptions_title", { defaultValue: "Permission levels:" })}
            </p>
            <ul className="space-y-0.5">
              <li>
                <strong>{t("sharing.role_reader", { defaultValue: "Reader" })}:</strong>{" "}
                {t("sharing.role_reader_desc_document", { defaultValue: "Can view the artifact" })}
              </li>
              <li>
                <strong>{t("sharing.role_editor", { defaultValue: "Editor" })}:</strong>{" "}
                {t("sharing.role_editor_desc_document", {
                  defaultValue: "Can view and edit the artifact",
                })}
              </li>
            </ul>
          </div>
        </div>

        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.close", { defaultValue: "Close" })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
